import { useQuery } from '@apollo/client/react/index.js';
import {
  AnimatedCounter,
  GlassBadge,
  GlassButton,
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
  GlassEmptyState,
  GlassInput,
  GlassTable,
  GlassTableBody,
  GlassTableCell,
  GlassTableHead,
  GlassTableHeaderCell,
  GlassTableRow,
  SkeletonLoader,
} from '@merritrac/shared/components';
import { Building2, Eye, Mail, Phone, Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { GET_CLIENTS } from '../lib/graphql/operations.js';

import type { ReactElement } from 'react';

/* ── GraphQL response types ── */

interface ClientNode {
  clientCode: string;
  clientName: string | null;
  contact: string | null;
  city: string | null;
  state: string | null;
  phone: string | null;
  email: string | null;
  status: string | null;
  type: string | null;
  inactive: number | null;
  onHold: number | null;
}

/* ── Badge helpers ── */

function statusVariant(
  status: string | null,
): 'default' | 'success' | 'danger' | 'warning' | 'info' {
  switch (status) {
    case 'CA':
      return 'success';
    case 'PR':
      return 'info';
    case 'CX':
      return 'danger';
    case 'RP':
      return 'warning';
    case 'PM':
      return 'info';
    case 'SB':
      return 'default';
    case null:
    default:
      return 'default';
  }
}

function statusLabel(status: string | null): string {
  switch (status) {
    case 'CA':
      return 'Active';
    case 'PR':
      return 'Prospect';
    case 'CX':
      return 'Cancelled';
    case 'SB':
      return 'Sandbox';
    case 'RP':
      return 'Repair';
    case 'PM':
      return 'Prop Mgr';
    case null:
      return '—';
    default:
      return status;
  }
}

function typeLabel(type: string | null): string {
  switch (type) {
    case 'CC':
      return 'Condo Corp';
    case 'RN':
      return 'Rental';
    case 'MC':
      return 'Mgmt Co';
    case 'CO':
      return 'Co-op';
    case 'SB':
      return 'Sandbox';
    case null:
    default:
      return type ?? '—';
  }
}

interface ClientEdge {
  node: ClientNode;
}

interface ClientConnection {
  totalCount: number;
  pageInfo: {
    hasNextPage: boolean;
    endCursor: string | null;
  };
  edges: ClientEdge[];
}

interface GetClientsData {
  clients: ClientConnection;
}

/* ── Page ── */

export function ClientsPage(): ReactElement {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const { data, loading, error } = useQuery<GetClientsData>(GET_CLIENTS, {
    variables: { first: 100 },
  });

  const clients: ClientNode[] = data?.clients.edges.map((e) => e.node) ?? [];

  const filtered = clients.filter((c) => {
    const query = search.toLowerCase();
    if (query === '') {
      return true;
    }
    const name = c.clientName ?? '';
    const code = c.clientCode;
    const contact = c.contact ?? '';
    const location = [c.city, c.state].filter(Boolean).join(', ');
    const status = statusLabel(c.status).toLowerCase();
    const type = typeLabel(c.type).toLowerCase();
    return (
      name.toLowerCase().includes(query) ||
      code.toLowerCase().includes(query) ||
      contact.toLowerCase().includes(query) ||
      location.toLowerCase().includes(query) ||
      status.includes(query) ||
      type.includes(query)
    );
  });

  const totalClients = data?.clients.totalCount ?? clients.length;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-text-secondary">
          Manage client accounts and contact information
        </p>
        <GlassButton
          variant="primary"
          size="md"
          onClick={() => {
            void navigate('/clients/new');
          }}
        >
          <Plus size={16} />
          New Client
        </GlassButton>
      </div>

      {/* Stats */}
      <GlassCard>
        <GlassCardContent className="flex items-center gap-4 py-4">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg shrink-0 bg-primary-soft">
            <Building2 size={20} className="text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-2xl font-semibold text-text leading-none">
              {loading ? (
                <SkeletonLoader className="w-12 h-7" />
              ) : (
                <AnimatedCounter value={totalClients} />
              )}
            </p>
            <p className="text-xs text-text-secondary mt-1 truncate">Total Clients</p>
          </div>
        </GlassCardContent>
      </GlassCard>

      {/* Table */}
      <GlassCard>
        <GlassCardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-base font-semibold text-text">All Clients</h2>
            <div className="relative">
              <Search
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none"
              />
              <GlassInput
                placeholder="Search clients…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
                className="pl-8 w-full sm:w-56"
                aria-label="Search clients"
              />
            </div>
          </div>
        </GlassCardHeader>

        {loading ? (
          <div className="p-5 space-y-3">
            <SkeletonLoader className="h-10 w-full" />
            <SkeletonLoader className="h-10 w-full" />
            <SkeletonLoader className="h-10 w-full" />
          </div>
        ) : error !== undefined ? (
          <div className="py-12 text-center text-sm text-danger">
            Failed to load clients: {error.message}
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-8">
            <GlassEmptyState
              icon={Building2}
              title="No clients found"
              description={
                search !== ''
                  ? 'Try adjusting your search to find what you are looking for.'
                  : 'There are no clients yet. Create one to get started.'
              }
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <GlassTable>
              <GlassTableHead>
                <GlassTableRow>
                  <GlassTableHeaderCell>Client</GlassTableHeaderCell>
                  <GlassTableHeaderCell>Status</GlassTableHeaderCell>
                  <GlassTableHeaderCell className="hidden sm:table-cell">Type</GlassTableHeaderCell>
                  <GlassTableHeaderCell className="hidden md:table-cell">
                    Contact
                  </GlassTableHeaderCell>
                  <GlassTableHeaderCell className="hidden lg:table-cell">
                    Location
                  </GlassTableHeaderCell>
                  <GlassTableHeaderCell className="hidden xl:table-cell">
                    Phone
                  </GlassTableHeaderCell>
                  <GlassTableHeaderCell className="hidden lg:table-cell">
                    Email
                  </GlassTableHeaderCell>
                  <GlassTableHeaderCell aria-label="Actions" />
                </GlassTableRow>
              </GlassTableHead>
              <GlassTableBody>
                {filtered.map((client) => {
                  const location = [client.city, client.state].filter(Boolean).join(', ');
                  return (
                    <GlassTableRow
                      key={client.clientCode}
                      className="cursor-pointer"
                      onClick={() => {
                        void navigate(`/clients/${encodeURIComponent(client.clientCode)}`);
                      }}
                    >
                      <GlassTableCell>
                        <div className="flex flex-col gap-0.5">
                          <span className="flex items-center gap-1.5 font-medium text-text">
                            {client.clientName ?? client.clientCode}
                            {client.inactive === 1 && (
                              <GlassBadge variant="default" className="text-xs">
                                Inactive
                              </GlassBadge>
                            )}
                            {client.onHold === 1 && client.inactive !== 1 && (
                              <GlassBadge variant="default" className="text-xs">
                                On Hold
                              </GlassBadge>
                            )}
                          </span>
                          <GlassBadge variant="default" className="w-fit text-xs">
                            {client.clientCode}
                          </GlassBadge>
                        </div>
                      </GlassTableCell>
                      <GlassTableCell>
                        {client.status !== null ? (
                          <GlassBadge variant={statusVariant(client.status)} className="text-xs">
                            {statusLabel(client.status)}
                          </GlassBadge>
                        ) : (
                          <span className="text-text-tertiary">—</span>
                        )}
                      </GlassTableCell>
                      <GlassTableCell className="hidden sm:table-cell">
                        {client.type !== null ? (
                          <GlassBadge variant="default" className="text-xs">
                            {typeLabel(client.type)}
                          </GlassBadge>
                        ) : (
                          <span className="text-text-tertiary">—</span>
                        )}
                      </GlassTableCell>
                      <GlassTableCell className="text-text-secondary hidden md:table-cell">
                        {client.contact ?? '—'}
                      </GlassTableCell>
                      <GlassTableCell className="text-text-secondary hidden lg:table-cell">
                        {location !== '' ? location : '—'}
                      </GlassTableCell>
                      <GlassTableCell className="hidden xl:table-cell">
                        {client.phone !== null ? (
                          <span className="flex items-center gap-1 text-text-secondary">
                            <Phone size={12} className="shrink-0" />
                            {client.phone}
                          </span>
                        ) : (
                          <span className="text-text-tertiary">—</span>
                        )}
                      </GlassTableCell>
                      <GlassTableCell className="hidden lg:table-cell">
                        {client.email !== null ? (
                          <span className="flex items-center gap-1 text-text-secondary">
                            <Mail size={12} className="shrink-0" />
                            <span className="truncate max-w-[180px]">{client.email}</span>
                          </span>
                        ) : (
                          <span className="text-text-tertiary">—</span>
                        )}
                      </GlassTableCell>
                      <GlassTableCell>
                        <GlassButton
                          variant="ghost"
                          size="sm"
                          aria-label={`View ${client.clientName ?? client.clientCode}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            void navigate(`/clients/${encodeURIComponent(client.clientCode)}`);
                          }}
                        >
                          <Eye size={14} />
                        </GlassButton>
                      </GlassTableCell>
                    </GlassTableRow>
                  );
                })}
              </GlassTableBody>
            </GlassTable>
          </div>
        )}
      </GlassCard>
    </div>
  );
}
